import * as XLSX from 'xlsx';
import { getTemplateSheetParamColumns } from './templateSendUtils';

const PHONE_REGEX = /^([1-9]{1}[0-9]{1,12})$/;
const MAX_BULK_VALIDATION_ERRORS = 25;

export function getBulkSheetParamColumns(template) {
  return getTemplateSheetParamColumns(template);
}

export function getBulkSheetRequiredColumns(template) {
  return ['phone_number', ...getBulkSheetParamColumns(template)];
}

export function normalizeExcelCell(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  return String(value).trim();
}

export function validateBulkPhoneNumber(value) {
  let raw = normalizeExcelCell(value).replace(/\s/g, '');
  if (!raw) {
    return { valid: false, error: 'Phone number is missing' };
  }
  if (!/^\d+$/.test(raw)) {
    return { valid: false, error: `Invalid phone number "${raw}"` };
  }
  if (raw.length === 10) {
    raw = `91${raw}`;
  }
  if (!PHONE_REGEX.test(raw) || raw.length < 10) {
    return {
      valid: false,
      error: `Invalid phone number "${raw}" (need 10 digits or country code)`,
    };
  }
  return { valid: true, normalized: raw };
}

export function validateBulkSheetRows(rows, paramColumns = []) {
  const errors = [];

  rows.forEach((row, index) => {
    const rowNum = index + 2;
    const phoneResult = validateBulkPhoneNumber(row.phone_number);
    if (!phoneResult.valid) {
      errors.push(`Row ${rowNum}: ${phoneResult.error}`);
    }

    paramColumns.forEach((col) => {
      if (!normalizeExcelCell(row[col])) {
        errors.push(`Row ${rowNum}: "${col}" is empty (required)`);
      }
    });
  });

  if (errors.length > MAX_BULK_VALIDATION_ERRORS) {
    return [
      ...errors.slice(0, MAX_BULK_VALIDATION_ERRORS),
      '...and more errors. Fix the above and re-upload.',
    ];
  }

  return errors;
}

export async function parseBulkExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('Excel file has no sheets');
  }

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return rows;
}

export const UPLOAD_SHEET_IGNORED_COLUMNS = Object.freeze([
  'row_number',
  'error_summary',
  'error_fields',
  'lead_id',
]);

export function stripUploadSheetColumns(row = {}) {
  const cleaned = { ...row };
  UPLOAD_SHEET_IGNORED_COLUMNS.forEach((key) => {
    delete cleaned[key];
  });
  return cleaned;
}

export function collectBulkSheetRowResults(rows, paramColumns = []) {
  return rows.map((rawRow, rowIndex) => {
    const rowNumber = rawRow?.row_number != null && rawRow.row_number !== ''
      ? Number(rawRow.row_number)
      : rowIndex + 2;
    const row = stripUploadSheetColumns(rawRow);
    const errors = [];

    const phoneResult = validateBulkPhoneNumber(row.phone_number);
    if (!phoneResult.valid) {
      errors.push({ field: 'phone_number', message: phoneResult.error });
    }

    paramColumns.forEach((col) => {
      if (col === 'phone_number') return;
      if (!normalizeExcelCell(row[col])) {
        errors.push({ field: col, message: `"${col}" is empty (required)` });
      }
    });

    return { rowNumber, row, errors };
  });
}

export function validateBulkSheetColumns(rows, requiredColumns, { strict = false } = {}) {
  if (!rows.length) {
    return ['Excel file has no data rows'];
  }

  const firstRow = stripUploadSheetColumns(rows[0]);
  const missing = requiredColumns.filter((col) => !(col in firstRow));
  if (missing.length) {
    return [`Missing required columns: ${missing.join(', ')}`];
  }

  if (strict) {
    const requiredSet = new Set(requiredColumns);
    const extra = Object.keys(firstRow).filter((col) => !requiredSet.has(col));
    if (extra.length) {
      return [`Unexpected columns (remove before upload): ${extra.join(', ')}`];
    }
  }

  return [];
}

export async function validateBulkExcelFile(file, template) {
  const requiredColumns = getBulkSheetRequiredColumns(template);
  const paramColumns = getBulkSheetParamColumns(template);

  let rows;
  try {
    rows = await parseBulkExcelFile(file);
  } catch (error) {
    return {
      valid: false,
      message: error?.message || 'Failed to read Excel file',
    };
  }

  const columnErrors = validateBulkSheetColumns(rows, requiredColumns, { strict: true });
  if (columnErrors.length) {
    return { valid: false, message: columnErrors.join('\n') };
  }

  const rowResults = collectBulkSheetRowResults(rows, paramColumns);
  const hasRowErrors = rowResults.some((entry) => entry.errors.length > 0);
  if (hasRowErrors) {
    return {
      valid: false,
      message: validateBulkSheetRows(rows, paramColumns).join('\n'),
      rowResults,
    };
  }

  return { valid: true, rowCount: rows.length };
}
